import React, { useState, useMemo } from "react";
import { Grid, useMediaQuery, Pagination } from "@mui/material";
import CustomContainer from "../layouts/container";
import HelmetTag from "../components/Shared/HelmetComponent";
import { Header, SubTitle } from "../components/Shared/TypographyComponent";
import SearchBar from "../components/Blog/SearchBar";
import FilterTabs from "../components/Blog/FilterTabs";
import BlogCard from "../components/Blog/BlogCard";
import BLOG_POSTS from "../data/blogPosts";

/*
Blog Landing Page
-----------------
Main blog page with search, filters, and post grid
*/

const POSTS_PER_PAGE = 9;

function Blog() {
  const Mobile = useMediaQuery((theme) => theme.breakpoints.only("xs"));

  // State for search and filters
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);

  // Filter and search posts
  const filteredPosts = useMemo(() => {
    let posts = BLOG_POSTS;

    // Filter by category
    if (activeFilter !== "All") {
      posts = posts.filter((post) => {
        // Handle both old category (string) and new categories (array) format
        if (Array.isArray(post.categories)) {
          return post.categories.includes(activeFilter);
        } else {
          return post.category === activeFilter;
        }
      });
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      posts = posts.filter((post) => {
        const titleMatch = post.title.toLowerCase().includes(query);
        const excerptMatch = post.excerpt.toLowerCase().includes(query);

        // Handle both old category (string) and new categories (array) format for search
        let categoryMatch = false;
        if (Array.isArray(post.categories)) {
          categoryMatch = post.categories.some((cat) =>
            cat.toLowerCase().includes(query)
          );
        } else {
          categoryMatch = post.category.toLowerCase().includes(query);
        }

        return titleMatch || excerptMatch || categoryMatch;
      });
    }

    return posts;
  }, [searchQuery, activeFilter]);

  // Get featured post
  const featuredPost = BLOG_POSTS.find((post) => post.featured);

  // Paginate posts
  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const paginatedPosts = filteredPosts.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  // Handle page change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Reset pagination when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, activeFilter]);

  return (
    <>
      {/* Blog Helmet Tag */}
      <HelmetTag
        title="Nasir Abdirahman - Blog"
        description="Essays and thoughts on engineering, AI, and product development"
        href="https://www.nasirabdirahman.com/blog"
      />

      <CustomContainer sx={{ pt: Mobile ? 10 : 15, pb: 10 }}>
        {/* Header */}
        <Grid container justifyContent="center" sx={{ mb: 5 }}>
          <Grid item xs={12} md={8}>
            <Header align="center" sx={{ color: "#17D1FA", mb: 2 }}>
              Blog
            </Header>
            <SubTitle align="center">
              My thoughts on engineering and product development.
            </SubTitle>
          </Grid>
        </Grid>

        {/* Search Bar */}
        <Grid container justifyContent="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <SearchBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />
          </Grid>
        </Grid>

        {/* Filter Tabs */}
        <Grid container justifyContent="center" sx={{ mb: 7 }}>
          <Grid item xs={12} md={10}>
            <FilterTabs
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
            />
          </Grid>
        </Grid>

        {/* Featured Post (only show when not filtering and not on mobile) */}
        {!searchQuery && activeFilter === "All" && featuredPost && (
          <Grid
            container
            justifyContent="center"
            sx={{ mb: 10, display: { xs: "none", sm: "flex" } }}
          >
            <Grid item xs={12} md={8}>
              <BlogCard post={featuredPost} featured />
            </Grid>
          </Grid>
        )}

        {/* Blog Posts Grid */}
        {paginatedPosts.length > 0 ? (
          <>
            <Grid container spacing={3} sx={{ mb: 7 }}>
              {paginatedPosts.map((post) => (
                <Grid item xs={12} sm={6} md={4} key={post.id}>
                  <BlogCard post={post} />
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Grid container justifyContent="center">
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "text.primary",
                      fontFamily: "Montserrat",
                      borderColor: "rgba(255, 255, 255, 0.1)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#17D1FA",
                        color: "#000000",
                        "&:hover": {
                          backgroundColor: "#17D1FA",
                        },
                      },
                    },
                  }}
                />
              </Grid>
            )}
          </>
        ) : (
          <Grid container justifyContent="center" sx={{ py: 10 }}>
            <SubTitle sx={{ opacity: 0.6 }}>
              No posts found. Try adjusting your search or filter.
            </SubTitle>
          </Grid>
        )}
      </CustomContainer>
    </>
  );
}

export default Blog;
